import React, { useState } from 'react';

/**
 * Composant YouTube optimisé avec technique de "facade"
 * Affiche une miniature cliquable au lieu de charger l'iframe YouTube immédiatement
 * Économise ~2000ms de temps d'exécution JavaScript au chargement initial
 * 
 * @param {string} videoId - ID de la vidéo YouTube
 * @param {string} title - Titre de la vidéo
 * @param {string} height - Hauteur (non utilisé, ratio 16:9 automatique)
 * @param {boolean} preload - Si true, optimise pour LCP avec fetchpriority="high"
 */
const YouTubeEmbedLite = ({ videoId, title = 'YouTube video', height = '400', preload = false }) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

  const handleClick = () => {
    setIsIframeLoaded(true);
  };

  // Si l'iframe est chargée, afficher la version complète
  if (isIframeLoaded) {
    return (
      <div style={{ 
        position: 'relative', 
        paddingBottom: '56.25%', 
        height: 0,
        overflow: 'hidden',
        maxWidth: '100%',
        backgroundColor: '#000'
      }}>
        <iframe
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0
          }}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&controls=1&loop=0&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  // Sinon, afficher la facade (miniature cliquable)
  return (
    <div 
      onClick={handleClick}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
      role="button"
      tabIndex={0}
      aria-label={`Jouer la vidéo: ${title}`}
      style={{ 
        position: 'relative', 
        paddingBottom: '56.25%', 
        height: 0,
        overflow: 'hidden',
        maxWidth: '100%',
        backgroundColor: '#000',
        cursor: 'pointer'
      }}
    >
      {/* Image de miniature YouTube - optimisée pour LCP si preload=true */}
      <img
        src={thumbnailUrl}
        alt={title}
        fetchPriority={preload ? 'high' : 'auto'}
        loading={preload ? 'eager' : 'lazy'}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
      {/* Bouton play au centre */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '68px',
        height: '48px',
        backgroundColor: 'rgba(255, 0, 0, 0.8)',
        borderRadius: '12px',
        transition: 'background-color 0.3s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 1)'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 0.8)'}
      >
        {/* Triangle play */}
        <svg height="100%" version="1.1" viewBox="0 0 68 48" width="100%">
          <path fill="#fff" d="M 22,14 L 22,34 L 36,24 z" />
        </svg>
      </div>

      {/* Indicateur de chargement au survol */}
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        padding: '4px 8px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: '#fff',
        fontSize: '12px',
        borderRadius: '4px'
      }}>
        Cliquer pour charger
      </div>
    </div>
  );
};

export default YouTubeEmbedLite;
